import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarRange, Download, Loader2, Users, Clapperboard, Send, DollarSign, Star, Target } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { nicheByKey } from "@/lib/niches";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" });

const Mini = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-lg border border-white/10 bg-black/30 p-4">
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      <Icon className={`h-3.5 w-3.5 ${accent}`} />
    </div>
    <div className="mt-2 text-2xl font-head font-extrabold tracking-tight">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>}
  </div>
);

export const MonthlyReport = ({ pin }) => {
  const [months, setMonths] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async (month = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/monthly-stats", { params: month ? { month } : {}, headers: { "X-Admin-Pin": pin } });
      setMonths(data.months || []);
      setStats(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { load(); }, [load]);

  const downloadReport = async () => {
    if (!stats) return;
    setDownloading(true);
    try {
      const resp = await api.get("/admin/monthly-report", {
        params: { month: stats.month },
        headers: { "X-Admin-Pin": pin },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `videoileo-report-${stats.month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const nicheEntries = stats ? Object.entries(stats.niche_signups || {}).sort((a, b) => b[1] - a[1]) : [];
  const nicheTotal = nicheEntries.reduce((s, [, n]) => s + n, 0) || 1;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="monthly-report">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="font-head font-bold text-lg flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-blue-400" /> Monthly Report
          {stats && <span className="text-sm font-normal text-zinc-500">· {monthLabel(stats.month)}</span>}
        </h2>
        <button
          onClick={downloadReport}
          disabled={downloading || !stats}
          data-testid="download-monthly-report-btn"
          className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download report (CSV)
        </button>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="month-pills">
        {months.map((m) => (
          <button
            key={m}
            onClick={() => load(m)}
            data-testid={`month-pill-${m}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              stats?.month === m
                ? "bg-blue-600 border-blue-500 text-white"
                : "bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10 hover:text-white"
            }`}
          >
            {monthLabel(m)}
          </button>
        ))}
      </div>

      {loading && !stats ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-400" /></div>
      ) : stats ? (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Mini icon={Users} accent="text-blue-400" label="New users" value={stats.new_users} />
            <Mini icon={Clapperboard} accent="text-pink-400" label="Videos created" value={stats.videos_created} />
            <Mini icon={Send} accent="text-cyan-400" label="Posts published" value={stats.posts_published} />
            <Mini icon={Star} accent="text-amber-400" label="Reviews" value={stats.reviews} />
            <Mini
              icon={DollarSign}
              accent="text-emerald-400"
              label="Revenue"
              value={fmtMoney(stats.revenue.total)}
              sub={`${fmtMoney(stats.revenue.subscriptions)} subs · ${fmtMoney(stats.revenue.credits)} credits`}
            />
          </div>

          {stats.totals && (
            <p className="mt-4 text-xs text-zinc-500 border-t border-white/5 pt-3" data-testid="all-time-totals">
              All-time: <span className="text-zinc-300 font-semibold">{stats.totals.users_total}</span> users ·{" "}
              <span className="text-zinc-300 font-semibold">{stats.totals.videos_total}</span> videos made ·{" "}
              <span className="text-zinc-300 font-semibold">{fmtMoney(stats.totals.revenue_total)}</span> total revenue
            </p>
          )}

          <div className="mt-5" data-testid="niche-signups">
            <p className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 mb-3">
              <Target className="h-3.5 w-3.5 text-purple-400" /> Niche funnel signups
            </p>
            {nicheEntries.length === 0 ? (
              <p className="text-sm text-zinc-500">No niche-card signups this month yet — they'll appear here when visitors join via the landing niche reel.</p>
            ) : (
              <div className="space-y-2">
                {nicheEntries.map(([k, n]) => (
                  <div key={k} data-testid={`niche-signup-row-${k}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-300">{nicheByKey(k)?.label || k}</span>
                      <span className="text-zinc-500">{n}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${Math.round((n / nicheTotal) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MonthlyReport;
